import React, { useState, useEffect } from "react";
import Layout from "../../../Layout";
import Notification from "./common/Notification";
import ClassesView from "./classes/ClassesView";
import ClassDetailsView from "./classes/ClassDetailsView";
import AssignmentDetailsView from "./assignments/AssignmentDetailsView";
import apiService from "../api/apiService";
import { addAnimationsToDocument } from "../utils/helpers";

/**
 * Main Student Dashboard component
 * Now supports different API services based on grade level
 */
const StudentDashboard = () => {
    // View state
    const [currentView, setCurrentView] = useState("classes"); // classes, classDetails, assignmentDetails

    // Data state
    const [classes, setClasses] = useState([]);
    const [enrolledClasses, setEnrolledClasses] = useState([]);
    const [availableClasses, setAvailableClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedAssignment, setSelectedAssignment] = useState(null);

    // UI state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({show: false, message: "", type: ""});

    // Initialize animations
    useEffect(() => {
        addAnimationsToDocument();
    }, []);

    // Initial data fetch
    useEffect(() => {
        fetchInitialData();
    }, []);

    // Fetch initial data (classes)
    const fetchInitialData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Always use the regular API service for fetching all classes
            const classesData = await apiService.getClasses();
            setClasses(classesData);
            console.log(classesData);

            // Filter enrolled and available classes
            const enrolled = classesData.filter(cls => cls.is_enrolled);
            const available = classesData.filter(cls => !cls.is_enrolled);

            setEnrolledClasses(enrolled);
            setAvailableClasses(available);
        } catch (err) {
            setError("Failed to fetch data. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle class selection
    const handleClassSelect = (classItem) => {
        setSelectedClass(classItem);
        setCurrentView("classDetails");
    };

    // Handle assignment selection
    const handleAssignmentSelect = async (assignment, classData) => {
        try {
            // Store the assignment details temporarily
            // The actual API call will be made in AssignmentDetailsView based on grade level
            setSelectedAssignment(assignment);

            // Make sure we have the class data available for grade-based decisions
            if (!selectedClass && classData) {
                setSelectedClass(classData);
            }

            setCurrentView("assignmentDetails");
        } catch (err) {
            showNotification("Failed to fetch assignment details.", "error");
            console.error(err);
        }
    };

    // Handle enrollment in a class
    const handleEnroll = async (classId, e) => {
        e?.stopPropagation(); // Prevent triggering the card click
        setLoading(true);
        try {
            await apiService.enrollInClass(classId);

            // Update the classes data
            const updatedClasses = classes.map(cls =>
                cls.class_id === classId ? {...cls, is_enrolled: true} : cls
            );

            setClasses(updatedClasses);

            // Update enrolled and available classes
            const enrolled = updatedClasses.filter(cls => cls.is_enrolled);
            const available = updatedClasses.filter(cls => !cls.is_enrolled);
            setEnrolledClasses(enrolled);
            setAvailableClasses(available);

            showNotification("Successfully enrolled in class!", "success");
        } catch (err) {
            showNotification("Failed to enroll in class.", "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle dropping a class
    const handleDrop = async (classId, e) => {
        e?.stopPropagation(); // Prevent triggering the card click

        if (!window.confirm("Are you sure you want to drop this class?")) {
            return;
        }

        setLoading(true);
        try {
            await apiService.dropClass(classId);

            // Update the classes data
            const updatedClasses = classes.map(cls =>
                cls.class_id === classId ? {...cls, is_enrolled: false} : cls
            );

            setClasses(updatedClasses);

            // Update enrolled and available classes
            const enrolled = updatedClasses.filter(cls => cls.is_enrolled);
            const available = updatedClasses.filter(cls => !cls.is_enrolled);
            setEnrolledClasses(enrolled);
            setAvailableClasses(available);

            // If we're on the details view of the class we're dropping, go back to the classes view
            if (selectedClass && selectedClass.class_id === classId) {
                setCurrentView("classes");
                setSelectedClass(null);
            }

            showNotification("Successfully dropped class.", "success");
        } catch (err) {
            showNotification("Failed to drop class.", "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        if (currentView === "assignmentDetails") {
            setCurrentView("classDetails");
            setSelectedAssignment(null);
        } else if (currentView === "classDetails") {
            setCurrentView("classes");
            setSelectedClass(null);
        }
    };

    // Show notification
    const showNotification = (message, type = "success") => {
        setNotification({
            show: true,
            message,
            type
        });

        // Hide notification after 3 seconds
        setTimeout(() => {
            setNotification({show: false, message: "", type: ""});
        }, 3000);
    };

    // Dismiss notification
    const dismissNotification = () => {
        setNotification({show: false, message: "", type: ""});
    };

    // Render the appropriate view
    const renderView = () => {
        switch (currentView) {
            case "classes":
                return (
                    <ClassesView
                        enrolledClasses={enrolledClasses}
                        availableClasses={availableClasses}
                        handleClassSelect={handleClassSelect}
                        handleEnroll={handleEnroll}
                        handleDrop={handleDrop}
                        loading={loading}
                        error={error}
                    />
                );
            case "classDetails":
                return (
                    <ClassDetailsView
                        selectedClass={selectedClass}
                        handleBack={handleBack}
                        handleAssignmentSelect={handleAssignmentSelect}
                        handleEnroll={handleEnroll}
                        handleDrop={handleDrop}
                        notification={notification}
                        showNotification={showNotification}
                    />
                );
            case "assignmentDetails":
                return (
                    <AssignmentDetailsView
                        selectedAssignment={selectedAssignment}
                        selectedClass={selectedClass}
                        handleBack={handleBack}
                        notification={notification}
                        showNotification={showNotification}
                    />
                );
            default:
                // Fallback view (shouldn't happen)
                return (
                    <div className="font-[poppins] min-h-screen flex items-center justify-center bg-gray-50">
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-gray-900">Something went wrong</h2>
                            <p className="mt-2 text-gray-600">Unable to load dashboard.</p>
                            <button
                                onClick={() => setCurrentView("classes")}
                                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#F17C45] hover:bg-[#e06a34]"
                            >
                                Return to Dashboard
                            </button>
                        </div>
                    </div>
                );
        }
    };

    return (
        <Layout>
            {renderView()}
            <Notification
                notification={notification}
                onDismiss={dismissNotification}
            />
        </Layout>
    );
};

export default StudentDashboard;